import type { ReactNode } from 'react';
import { Text } from '../Text/Text';
import styles from './Badge.module.css';

/**
 * Figma: `Badge` (50:3073) — "Status label. Type controls semantic color.
 * Use for status indicators, tags, and labels."
 *
 * `neutral` is the warning/caution variant (yellow) — `gray` is the plain
 * default look. Easy to mix up: Figma renamed the old default-gray variant
 * from `Neutral` to `Gray` and repurposed `Neutral` for warning.
 *
 * Design update (2026-08-24): the badge lost its border and its pill shape —
 * `radius/sm` (6px) now, square-cornered next to `Button` and `Select` rather
 * than a lozenge — and its label moved from Regular to Medium. See
 * Badge.module.css and plan.md §6.
 *
 * Reverted later the same day: the label is `Text xs/Regular` again, so the
 * weight is back to Regular. The border and radius changes stand.
 *
 * That revert did not reach the code until 2026-09-14 — the label kept rendering
 * `weight="medium"` for three weeks while all five Figma variants bound
 * `font-weight/regular`. It was a width-only defect, since 10/10 Medium and
 * 10/10 Regular share a line box, which is why the height fixture never caught
 * it and the widths were deliberately left unasserted until it was settled.
 */
export type BadgeType = 'gray' | 'negative' | 'positive' | 'info' | 'neutral';

export type BadgeProps = {
  type?: BadgeType;
  className?: string;
  children?: ReactNode;
  /**
   * Spoken name, for a badge whose text is too terse to stand on its own — the
   * bare "2" on the sidebar's `Crisis alerts` row is the case this exists for.
   * Same treatment `Counter` gives its `label`. Not in Figma, which has no way
   * to say anything that is not drawn.
   */
  'aria-label'?: string;
};

const surfaceClass: Record<BadgeType, string> = {
  gray: styles.gray,
  negative: styles.negative,
  positive: styles.positive,
  info: styles.info,
  neutral: styles.neutral,
};

const textColor = {
  gray: 'primary',
  negative: 'error',
  positive: 'success',
  info: 'link',
  neutral: 'warning',
} as const;

export function Badge({
  type = 'gray',
  className,
  children,
  'aria-label': ariaLabel,
}: BadgeProps) {
  const classes = [styles.badge, surfaceClass[type], className].filter(Boolean).join(' ');

  return (
    /* `role="img"` so the `aria-label` is honoured: a bare `<span>` with a name
       and no role is skipped by most screen readers. */
    <span className={classes} role={ariaLabel ? 'img' : undefined} aria-label={ariaLabel}>
      <Text size="xs" leading="none" weight="regular" color={textColor[type]}>
        {children}
      </Text>
    </span>
  );
}
